import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { apiService } from '../../services/api';
import ProcessingSettingsPanel, { DEFAULT_PARAMS, PROVIDER_TYPE_LABELS } from './ProcessingSettingsPanel';
import './BulkBuild.css';

const STORAGE_KEY = 'bulkBuild_batch';
const PARAMS_STORAGE_KEY = 'graphBuilder_globalParams';

function BulkBuildPage() {
  const navigate = useNavigate();

  // Config form state
  const [baseName, setBaseName] = useState('');
  const [workspaceId, setWorkspaceId] = useState('');
  const [description, setDescription] = useState('');
  const [autoGeneratePrompt, setAutoGeneratePrompt] = useState(true);
  const [workspaces, setWorkspaces] = useState([]);

  // Processing settings (shared with BuildGraphPage via localStorage)
  const [globalParams, setGlobalParams] = useState(() => {
    const saved = localStorage.getItem(PARAMS_STORAGE_KEY);
    if (saved) {
      try { return { ...DEFAULT_PARAMS, ...JSON.parse(saved) }; } catch (e) { /* ignore */ }
    }
    return { ...DEFAULT_PARAMS };
  });

  // LLM Providers
  const [llmProviders, setLlmProviders] = useState([]);
  const modelOptions = useMemo(() => {
    return llmProviders.map((p) => ({
      value: `provider::${p.id}::${p.model_name}`,
      label: `${p.model_name} (${PROVIDER_TYPE_LABELS[p.provider_type] || p.provider_type}${p.name ? ' - ' + p.name : ''})`,
    }));
  }, [llmProviders]);

  // File upload state
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [config, setConfig] = useState({
    maxDocumentSizeMB: 50,
    maxDocumentsPerUpload: 100,
    allowedDocumentTypes: ['pdf', 'docx', 'txt', 'doc'],
  });
  const fileInputRef = useRef(null);

  // Submission state
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);

  // Batch monitoring state
  const [batch, setBatch] = useState(null);
  const [batchStatus, setBatchStatus] = useState(null);
  const [pollingActive, setPollingActive] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const pollingRef = useRef(null);
  const elapsedRef = useRef(null);

  // Resolve provider::ID::model format for API calls
  const resolveParamsForApi = useCallback((params) => {
    if (!params?.model?.startsWith('provider::')) return params;
    const parts = params.model.split('::');
    return { ...params, model: parts[2], provider_id: parts[1] };
  }, []);

  // Load workspaces, config, and providers on mount
  useEffect(() => {
    const init = async () => {
      try {
        const [wsRes, cfgRes, provRes] = await Promise.all([
          apiService.getWorkspacesForDropdown(),
          apiService.getDocumentUploadConfig(),
          apiService.listLLMProviders(),
        ]);
        if (wsRes.success) setWorkspaces(wsRes.workspaces || []);
        if (cfgRes.success) setConfig(cfgRes.config || cfgRes);
        setLlmProviders(provRes.providers || []);
      } catch (err) {
        console.error('Init error:', err);
      }
    };
    init();

    // Restore batch from localStorage if navigating back
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.batch && parsed.batch.length > 0) {
          setBatch(parsed.batch);
          setStartTime(parsed.startTime ? new Date(parsed.startTime) : null);
        }
      } catch (e) { /* ignore */ }
    }
  }, []);

  // Auto-select default provider
  useEffect(() => {
    if (!llmProviders.length) return;
    const defaultProvider = llmProviders.find((p) => p.is_default) || llmProviders[0];
    if (defaultProvider && !globalParams.model?.startsWith('provider::')) {
      setGlobalParams((prev) => ({
        ...prev,
        model: `provider::${defaultProvider.id}::${defaultProvider.model_name}`,
      }));
    }
  }, [llmProviders]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist globalParams to localStorage
  useEffect(() => {
    localStorage.setItem(PARAMS_STORAGE_KEY, JSON.stringify(globalParams));
  }, [globalParams]);

  // Polling for batch status
  const pollStatus = useCallback(async (batchItems) => {
    const docIds = batchItems.map(b => b.document_id);
    if (!docIds.length) return;
    try {
      const res = await apiService.getBulkBuildStatus(docIds);
      if (res.success) {
        setBatchStatus(res);
        // Check if all terminal
        const summary = res.summary || {};
        const terminal = (summary.processed || 0) + (summary.failed || 0);
        if (terminal >= summary.total) {
          setPollingActive(false);
        }
      }
    } catch (err) {
      console.error('Poll error:', err);
    }
  }, []);

  useEffect(() => {
    if (pollingActive && batch) {
      pollStatus(batch);
      pollingRef.current = setInterval(() => pollStatus(batch), 8000);
    }
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [pollingActive, batch, pollStatus]);

  // Elapsed timer
  useEffect(() => {
    if (pollingActive && startTime) {
      elapsedRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startTime.getTime()) / 1000));
      }, 1000);
    }
    return () => { if (elapsedRef.current) clearInterval(elapsedRef.current); };
  }, [pollingActive, startTime]);

  // Also poll once when batch is restored from localStorage
  useEffect(() => {
    if (batch && !pollingActive) {
      pollStatus(batch).then(() => {
        // Check if still needs polling
        if (batchStatus) {
          const s = batchStatus.summary || {};
          const terminal = (s.processed || 0) + (s.failed || 0);
          if (terminal < s.total) setPollingActive(true);
        } else {
          setPollingActive(true);
        }
      });
    }
  }, [batch]); // eslint-disable-line react-hooks/exhaustive-deps

  // File handling
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(prev => [...prev, ...files]);
    setError(null);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files) {
      setSelectedFiles(prev => [...prev, ...Array.from(e.dataTransfer.files)]);
      setError(null);
    }
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Submit
  const handleSubmit = async () => {
    if (!baseName.trim()) { setError('Base graph name is required'); return; }
    if (!workspaceId) { setError('Please select a workspace'); return; }
    if (selectedFiles.length === 0) { setError('Please select at least one file'); return; }

    // Validate files
    const allowed = config.allowedDocumentTypes || ['pdf', 'docx', 'txt', 'doc'];
    for (const f of selectedFiles) {
      const ext = f.name.split('.').pop().toLowerCase();
      if (!allowed.includes(ext)) {
        setError(`File type .${ext} not allowed: ${f.name}`);
        return;
      }
    }

    // Per-file size check
    const maxBytes = (config.maxDocumentSizeMB || 50) * 1024 * 1024;
    for (const f of selectedFiles) {
      if (f.size > maxBytes) {
        setError(`File "${f.name}" exceeds ${config.maxDocumentSizeMB}MB limit`);
        return;
      }
    }

    setError(null);
    setUploading(true);
    setUploadProgress(0);

    try {
      const params = {
        ...resolveParamsForApi(globalParams),
        auto_generate_prompt: autoGeneratePrompt,
      };

      const result = await apiService.bulkBuild(
        baseName.trim(),
        workspaceId,
        selectedFiles,
        params,
        description.trim(),
        (progressEvent) => {
          const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(pct);
        },
      );

      if (result.success && result.batch) {
        const now = new Date();
        setBatch(result.batch);
        setStartTime(now);
        setPollingActive(true);
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          batch: result.batch,
          startTime: now.toISOString(),
        }));
        if (result.errors && result.errors.length > 0) {
          setError(`${result.total_created} graphs created. ${result.total_errors} file(s) had errors.`);
        }
      } else {
        setError(result.error || 'Bulk build failed');
      }
    } catch (err) {
      console.error('Bulk build error:', err);
      setError(err.response?.data?.error || 'Failed to start bulk build');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleNewBatch = () => {
    setBatch(null);
    setBatchStatus(null);
    setPollingActive(false);
    setStartTime(null);
    setElapsed(0);
    setSelectedFiles([]);
    setBaseName('');
    setDescription('');
    setError(null);
    localStorage.removeItem(STORAGE_KEY);
  };

  const [cancelling, setCancelling] = useState(false);

  const handleCancelBatch = async () => {
    if (!batch || !window.confirm(
      'Cancel this bulk build session?\n\n' +
      '• Documents that are uploaded/queued/processing will be canceled\n' +
      '• Graphs with no processed documents will be deleted\n' +
      '• Already processed graphs will be kept\n\n' +
      'This cannot be undone.'
    )) return;

    try {
      setCancelling(true);
      setError(null);
      const docIds = batch.map(b => b.document_id);
      const result = await apiService.cancelBulkBuild(docIds);

      if (result.success) {
        const msg = `Canceled ${result.cancelled_documents} document(s), deleted ${result.deleted_graphs} graph(s), kept ${result.kept_graphs} graph(s).`;
        handleNewBatch();
        setError(null);
        alert(msg);
      } else {
        setError(result.error || 'Cancel failed');
      }
    } catch (err) {
      console.error('Cancel bulk build error:', err);
      setError(err.response?.data?.error || 'Failed to cancel bulk build');
    } finally {
      setCancelling(false);
    }
  };

  const formatElapsed = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  // Merge batch items with live status
  const batchItems = useMemo(() => {
    if (!batch) return [];
    const statusMap = {};
    if (batchStatus?.documents) {
      batchStatus.documents.forEach(d => { statusMap[d.document_id] = d; });
    }
    const statusOrder = { processing: 0, queued: 1, uploaded: 1, processed: 2, failed: 3 };
    return batch.map(b => ({
      ...b,
      ...(statusMap[b.document_id] || {}),
    })).sort((a, b) => (statusOrder[a.status] ?? 4) - (statusOrder[b.status] ?? 4));
  }, [batch, batchStatus]);

  const summary = batchStatus?.summary || null;

  // ---- Render ----

  // Monitoring view (post-submission)
  if (batch) {
    const processedPct = summary ? Math.round(((summary.processed || 0) / summary.total) * 100) : 0;
    const isComplete = summary && ((summary.processed || 0) + (summary.failed || 0)) >= summary.total;

    return (
      <div className="bulk-build-page">
        <div className="page-header">
          <button className="btn-back" onClick={() => navigate('/user-home/graphs')}>Back to Graphs</button>
          <h1>Bulk Build Progress</h1>
        </div>

        {error && <div className="alert alert-warning">{error}</div>}

        {/* Summary bar */}
        {summary && (
          <div className="batch-summary">
            <div className="summary-stats">
              <div className="stat">
                <span className="stat-value">{summary.total}</span>
                <span className="stat-label">Total</span>
              </div>
              <div className="stat stat-queued">
                <span className="stat-value">{summary.queued || 0}</span>
                <span className="stat-label">Queued</span>
              </div>
              <div className="stat stat-processing">
                <span className="stat-value">{summary.processing || 0}</span>
                <span className="stat-label">Processing</span>
              </div>
              <div className="stat stat-processed">
                <span className="stat-value">{summary.processed || 0}</span>
                <span className="stat-label">Processed</span>
              </div>
              <div className="stat stat-failed">
                <span className="stat-value">{summary.failed || 0}</span>
                <span className="stat-label">Failed</span>
              </div>
              <div className="stat">
                <span className="stat-value">{formatElapsed(elapsed)}</span>
                <span className="stat-label">Elapsed</span>
              </div>
            </div>
            <div className="progress-bar-container">
              <div className="progress-bar-fill" style={{ width: `${processedPct}%` }} />
            </div>
            <div className="progress-bar-label">{processedPct}% complete ({summary.processed || 0}/{summary.total})</div>
          </div>
        )}

        {/* Actions */}
        <div className="batch-actions">
          <button className="btn-secondary" onClick={() => pollStatus(batch)}>Refresh</button>
          {isComplete ? (
            <button className="btn-primary" onClick={handleNewBatch}>Start New Batch</button>
          ) : (
            <button
              className="btn-danger"
              onClick={handleCancelBatch}
              disabled={cancelling}
            >
              {cancelling ? 'Canceling...' : 'Cancel Batch'}
            </button>
          )}
        </div>

        {/* Documents table */}
        <div className="batch-table-container">
          <table className="batch-table">
            <thead>
              <tr>
                <th>#</th>
                <th>File Name</th>
                <th>Graph Name</th>
                <th>Status</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              {batchItems.map((item, idx) => (
                <tr key={item.document_id} className={`status-row status-${item.status}`}>
                  <td>{idx + 1}</td>
                  <td>{item.filename}</td>
                  <td>
                    <Link to={`/user-home/graphs/build/${item.graph_id}`} className="graph-link">
                      {item.graph_name}
                    </Link>
                  </td>
                  <td>
                    <span className={`status-badge badge-${item.status}`}>
                      {item.status === 'processing' && <span className="spinner" />}
                      {item.status}
                    </span>
                  </td>
                  <td className="error-cell">{item.error_message || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Config form view (pre-submission)
  return (
    <div className="bulk-build-page">
      <div className="page-header">
        <button className="btn-back" onClick={() => navigate('/user-home/graphs')}>Back to Graphs</button>
        <h1>Bulk Graph Builder</h1>
        <p className="page-description">
          Upload multiple documents to create one graph per document, all sharing the same settings.
        </p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Configuration */}
      <div className="config-section">
        <h2>Configuration</h2>
        <div className="config-grid">
          <div className="config-item">
            <label>Base Graph Name <span className="required">*</span></label>
            <input
              type="text"
              value={baseName}
              onChange={(e) => setBaseName(e.target.value)}
              placeholder='e.g. "AML Policy" produces "AML Policy - filename"'
            />
          </div>
          <div className="config-item">
            <label>Workspace <span className="required">*</span></label>
            <select value={workspaceId} onChange={(e) => setWorkspaceId(e.target.value)}>
              <option value="">Select Workspace</option>
              {workspaces.map(ws => (
                <option key={ws.workspaceId} value={ws.workspaceId}>{ws.workspaceName}</option>
              ))}
            </select>
          </div>
          <div className="config-item full-width">
            <label>Graph Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Description applied to all created graphs"
              rows={2}
            />
          </div>
          <div className="config-item checkbox-row">
            <label>
              <input
                type="checkbox"
                checked={autoGeneratePrompt}
                onChange={(e) => setAutoGeneratePrompt(e.target.checked)}
              />
              Auto-generate custom prompt for each document
            </label>
            <span className="hint">Uses AI to create a document-specific extraction prompt before processing</span>
          </div>
        </div>
      </div>

      {/* Processing Settings */}
      <ProcessingSettingsPanel
        globalParams={globalParams}
        setGlobalParams={setGlobalParams}
        modelOptions={modelOptions}
        showActions={false}
        collapsible={false}
        defaultOpen={true}
      />

      {/* File Upload */}
      <div className="upload-section">
        <h2>Upload Documents</h2>
        <p className="section-description">
          Each file will create its own graph named <strong>{baseName || '<base name>'} - &lt;filename&gt;</strong>.
        </p>

        <div
          className={`drop-zone ${dragActive ? 'drag-active' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={config.allowedDocumentTypes.map(t => `.${t}`).join(',')}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <div className="drop-zone-content">
            <div className="upload-icon">+</div>
            <p className="drop-zone-text">
              <strong>Click to upload</strong> or drag and drop
            </p>
            <p className="drop-zone-hint">
              PDF, DOCX, TXT, or DOC (max {config.maxDocumentSizeMB}MB per file)
            </p>
          </div>
        </div>

        {selectedFiles.length > 0 && (
          <div className="selected-files">
            <div className="files-header">
              <span>{selectedFiles.length} file(s) selected</span>
              <button className="btn-link" onClick={() => setSelectedFiles([])}>Clear all</button>
            </div>
            <ul className="file-list">
              {selectedFiles.map((f, i) => (
                <li key={i} className="file-item">
                  <span className="file-name">{f.name}</span>
                  <span className="file-size">{(f.size / 1024).toFixed(1)} KB</span>
                  <button className="btn-remove" onClick={() => removeFile(i)} title="Remove">x</button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {uploading && (
          <div className="upload-progress">
            <div className="progress-bar-container">
              <div className="progress-bar-fill" style={{ width: `${uploadProgress}%` }} />
            </div>
            <p className="progress-text">{uploadProgress}% Uploading...</p>
          </div>
        )}
      </div>

      {/* Submit */}
      <div className="submit-section">
        <button
          className="btn-primary btn-large"
          onClick={handleSubmit}
          disabled={uploading || !baseName.trim() || !workspaceId || selectedFiles.length === 0}
        >
          {uploading ? 'Creating Graphs...' : `Start Bulk Build (${selectedFiles.length} graph${selectedFiles.length !== 1 ? 's' : ''})`}
        </button>
      </div>
    </div>
  );
}

export default BulkBuildPage;
